// src/components/RunComparisonBar.tsx
import React, { useMemo, useState } from "react";
import { getRunDisplayName, type RunHistoryItem } from "../lib/runHistory";

type RunComparisonBarProps = {
  currentRun: RunHistoryItem | null;
  comparisonRun: RunHistoryItem | null;
  availableRuns?: RunHistoryItem[];
  onComparisonRunChange?: (runId: string) => void;
};

export default function RunComparisonBar({
  currentRun,
  comparisonRun,
  availableRuns = [],
  onComparisonRunChange,
}: RunComparisonBarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hasComparison = Boolean(comparisonRun);

  const previousRuns = useMemo(
    () => availableRuns.filter((run) => run.run_id !== currentRun?.run_id),
    [availableRuns, currentRun?.run_id]
  );

  return (
    <div style={styles.wrapper}>
      <div style={styles.summaryRow}>
        <div>
          <p style={styles.eyebrow}>Reporting-period comparison</p>
          <div style={styles.titleRow}>
            <span style={styles.current}>{getRunDisplayName(currentRun)}</span>
            <span style={styles.vs}>compared to</span>
            <span style={hasComparison ? styles.previous : styles.muted}>
              {hasComparison ? getRunDisplayName(comparisonRun) : "No previous run yet"}
            </span>
          </div>
        </div>

        {previousRuns.length > 0 && onComparisonRunChange && (
          <button
            type="button"
            className="no-print"
            onClick={() => setDrawerOpen((open) => !open)}
            style={styles.timelineButton}
          >
            {drawerOpen ? "Hide timeline" : "Choose benchmark"}
          </button>
        )}
      </div>

      {drawerOpen && previousRuns.length > 0 && onComparisonRunChange && (
        <div className="no-print" style={styles.drawer}>
          <div style={styles.drawerHeader}>
            <div>
              <p style={styles.drawerEyebrow}>Run timeline</p>
              <p style={styles.drawerTitle}>Select a previous completed run to compare against.</p>
            </div>
          </div>

          <div style={styles.timelineTrack}>
            {previousRuns.map((run, index) => {
              const selected = run.run_id === comparisonRun?.run_id;

              return (
                <button
                  key={run.run_id}
                  type="button"
                  onClick={() => {
                    onComparisonRunChange(run.run_id);
                    setDrawerOpen(false);
                  }}
                  style={{
                    ...styles.runCard,
                    ...(selected ? styles.runCardSelected : {}),
                  }}
                >
                  <span style={selected ? styles.timelineDotSelected : styles.timelineDot} />
                  <span style={styles.runIndex}>Previous run {index + 1}</span>
                  <span style={styles.runName}>{getRunDisplayName(run)}</span>
                  <span style={styles.runMeta}>Completed benchmark</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 16,
    background: "#ffffff",
    marginBottom: 16,
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "center",
    flexWrap: "wrap",
  },
  eyebrow: {
    margin: 0,
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  titleRow: {
    marginTop: 6,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
    fontSize: 14,
  },
  current: {
    fontWeight: 800,
    color: "#0f172a",
  },
  vs: {
    color: "#64748b",
  },
  previous: {
    fontWeight: 700,
    color: "#334155",
  },
  muted: {
    color: "#94a3b8",
    fontWeight: 600,
  },
  timelineButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 13,
    background: "#f8fafc",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  },
  drawer: {
    marginTop: 14,
    borderTop: "1px solid #e2e8f0",
    paddingTop: 14,
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginBottom: 12,
  },
  drawerEyebrow: {
    margin: 0,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  drawerTitle: {
    margin: "4px 0 0",
    color: "#475569",
    fontSize: 13,
    fontWeight: 650,
  },
  timelineTrack: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 10,
  },
  runCard: {
    position: "relative",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    background: "#ffffff",
    padding: "12px 12px 12px 34px",
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.03)",
  },
  runCardSelected: {
    border: "1px solid #2563eb",
    background: "#eff6ff",
  },
  timelineDot: {
    position: "absolute",
    left: 12,
    top: 16,
    width: 10,
    height: 10,
    borderRadius: 999,
    background: "#cbd5e1",
  },
  timelineDotSelected: {
    position: "absolute",
    left: 12,
    top: 16,
    width: 10,
    height: 10,
    borderRadius: 999,
    background: "#2563eb",
    boxShadow: "0 0 0 4px #dbeafe",
  },
  runIndex: {
    display: "block",
    color: "#64748b",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  runName: {
    display: "block",
    marginTop: 4,
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 850,
    lineHeight: 1.25,
  },
  runMeta: {
    display: "block",
    marginTop: 4,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 650,
  },
};
